/**
 * Login & Link Player ID flow version, per casino (PRD: "Login & Link Player ID flow
 * (LinkPlayerflowVersion 1 & 2)").
 *
 * Driven only by `LinkPlayerflowVersion` on the casino object (Mongo `casinos`, passed
 * through GetAllCasinos as-is and persisted as `casinoSchema`), same pattern as
 * `showPOP` / `showLoadingReels`. Number and string are the same value:
 *
 *  - 2 or '2'                                  -> Version 2: Link Player ID in the
 *                                                 Explore drawer (no full-screen step)
 *  - 1, '1', missing, null or anything else    -> Version 1: today's full-screen step
 *
 * Version 2 hand-off: ChooseCasinos does not redirect to /auth/link-player; it leaves
 * a one-time "Link Player due" note for this casino entry and opens Explore. Explore's
 * LinkPlayerDrawerHost opens the drawer once (after Random Outcomes, never with it) and
 * uses the note up, so staying in the casino never reopens it. No casino ids here.
 */

export const LINK_PLAYER_FLOW_FIELD = 'LinkPlayerflowVersion';
export const LINK_PLAYER_DUE_KEY = 'slotcheck.linkPlayerDue';
/** Fired (on window) when a new due note is written, so a mounted Explore re-checks. */
export const LINK_PLAYER_DUE_EVENT = 'slotcheck:linkPlayerDue';

export type LinkPlayerFlowVersion = 1 | 2;
export type LinkPlayerFlowCasino = { LinkPlayerflowVersion?: unknown } | null | undefined;

/** 2 / '2' (whitespace ignored) -> 2; everything else -> 1. */
export const parseLinkPlayerFlowVersion = (value: unknown): LinkPlayerFlowVersion => {
  if (value === 2) return 2;
  if (typeof value === 'string' && value.trim() === '2') return 2;
  return 1;
};

export const getLinkPlayerFlowVersion = (casino: LinkPlayerFlowCasino): LinkPlayerFlowVersion =>
  !!casino && typeof casino === 'object'
    ? parseLinkPlayerFlowVersion(casino.LinkPlayerflowVersion)
    : 1;

/** True when this casino uses the drawer flow (Version 2). */
export const isLinkPlayerDrawerFlow = (casino: LinkPlayerFlowCasino): boolean =>
  getLinkPlayerFlowVersion(casino) === 2;

// ---------------------------------------------------------------------------
// One-time "Link Player due on this casino entry" note: ChooseCasinos -> Explore.
// ---------------------------------------------------------------------------

export type LinkPlayerDue = { casinoId: number; entry: string };

function defaultSessionStorage(): Storage | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
  } catch {
    return null;
  }
}

let entrySeq = 0;
const newEntryId = (): string => `${Date.now().toString(36)}-${(entrySeq += 1)}`;

/** Version 2 casino entry by a player who isn't linked + verified here. */
export function markLinkPlayerDue(
  casinoId: number,
  storage: Storage | null = defaultSessionStorage()
): void {
  try {
    const due: LinkPlayerDue = { casinoId: Number(casinoId), entry: newEntryId() };
    storage?.setItem(LINK_PLAYER_DUE_KEY, JSON.stringify(due));
  } catch {
    // blocked storage -> no prompt this entry (Explore still works)
  }
  try {
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new Event(LINK_PLAYER_DUE_EVENT));
    }
  } catch {
    // ignore
  }
}

export function peekLinkPlayerDue(
  storage: Storage | null = defaultSessionStorage()
): LinkPlayerDue | null {
  try {
    const raw = storage?.getItem(LINK_PLAYER_DUE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<LinkPlayerDue> | null;
    const casinoId = Number(v?.casinoId);
    if (!v || !Number.isFinite(casinoId) || typeof v.entry !== 'string') return null;
    return { casinoId, entry: v.entry };
  } catch {
    return null;
  }
}

/** Used up when the drawer opens, and cleared at the start of every casino entry. */
export function clearLinkPlayerDue(storage: Storage | null = defaultSessionStorage()): void {
  try {
    storage?.removeItem(LINK_PLAYER_DUE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Explore: open the Link Player drawer now?
 *  - only with a due note for the casino on screen;
 *  - never while Random Outcomes is still due or on screen (it always comes first);
 *  - never at a Version 1 casino, or for a linked + verified player / casino account
 *    (ChooseCasinos never writes the note for them; checked again here in case the
 *    linked state changed since).
 */
export function shouldOpenLinkPlayerDrawer(input: {
  due: LinkPlayerDue | null;
  casino: (LinkPlayerFlowCasino & { casinoId?: number | string | null }) | null | undefined;
  randomOutcomesPending: boolean;
  randomOutcomesActive: boolean;
  isCasinoAccount: boolean;
  isLinkedAndVerified: boolean;
}): boolean {
  const { due, casino } = input;
  if (!due || !casino) return false;
  if (Number(casino.casinoId) !== due.casinoId) return false;
  if (!isLinkPlayerDrawerFlow(casino)) return false;
  if (input.randomOutcomesPending || input.randomOutcomesActive) return false;
  if (input.isCasinoAccount || input.isLinkedAndVerified) return false;
  return true;
}

/** Linked here and not 'unverified' (pending counts as linked, exactly as today). */
export function isLinkedAndVerifiedFor(
  linkedAccounts: Array<{ casinoId?: number | string; verification?: string }> | null | undefined,
  casinoId: number | string | null | undefined
): boolean {
  if (!linkedAccounts || casinoId == null) return false;
  const item = linkedAccounts.find((a) => Number(a?.casinoId) === Number(casinoId));
  return Boolean(item) && item?.verification !== 'unverified';
}

// ---------------------------------------------------------------------------
// Drawer frame: never over the casino header, never under the tab bar / keyboard.
// ---------------------------------------------------------------------------

export type LinkPlayerDrawerFrame = { top: number; bottom: number; height: number };

/**
 * Where the Link Player drawer may live, in px from the layout viewport edges:
 *  - top    = bottom edge of the casino header (logo / search / avatar), so the header
 *             stays fully visible and undimmed;
 *  - bottom = gap under the drawer: the tab bar, or the keyboard when it is up
 *             (visual viewport bottom), whichever is higher on screen.
 * The drawer's content scrolls inside this frame when it doesn't fit.
 */
export function computeLinkPlayerDrawerFrame(input: {
  viewportHeight: number;
  headerBottom?: number | null;
  tabBarTop?: number | null;
  visualViewportBottom?: number | null;
}): LinkPlayerDrawerFrame {
  const vh = Math.max(0, Number(input.viewportHeight) || 0);
  const clamp = (n: number) => Math.min(Math.max(n, 0), vh);
  const top = clamp(Number(input.headerBottom) || 0);
  let bottomEdge = vh;
  const tab = Number(input.tabBarTop);
  if (Number.isFinite(tab) && tab > top && tab < bottomEdge) bottomEdge = tab;
  const vv = Number(input.visualViewportBottom);
  if (Number.isFinite(vv) && vv > top && vv < bottomEdge) bottomEdge = vv;
  bottomEdge = clamp(bottomEdge);
  return { top, bottom: vh - bottomEdge, height: Math.max(0, bottomEdge - top) };
}

/** Explore (the casino property screen) is the only place either drawer may open. */
export const isExplorePath = (pathname: string | null | undefined): boolean =>
  typeof pathname === 'string' && /^\/tabs\/home(\/|$)/.test(pathname);
